<template>
  <div>
    <div class='px-24 mb-px-16 overflow-auto' :style='isDesktop || formData.isUIPopup ? `height: ${formData.heightForm}px` : ""'>
      <div :class="formData.labelClass?.stepsLabel">STEP {{ indexCurrentStep }} OF 5</div>
      <div :class="formData.labelClass?.titleClass">Your Style</div>
      <div :class="[formData.labelClass?.subTitleClass, formData.labelClass?.divider]">
        {{
          fromTripPlan
            ? "You've picked a great trip, now let's make it perfect for you. Are there any changes to the itinerary to make it exactly what you're looking for?"
            : "You're well on your way to getting a fully-customised trip, just for you. Here's your opportunity to tell us anything that would make it even better."
        }}
      </div>
      <div class="pt-16">
        <div class="relative">
          <form class="content-form">
            <label :class="formData.labelClass.cssLabelField" ref="monthOfTravelRef">
              {{ labelFieldStyle }} <!--<span :class="formData.labelClass?.cssLabelOptionField">(*optional)</span>-->
            </label>
            <div :class="formData.labelClass.cssLabelField">
              <textarea
                placeholder="..."
                v-model="dataStep.suggestion"
                class="db wp-100 h-240 mb-h-80 pt-13 pb-11 pl-16 pr-40 br-6 bd-1 bd-gray-medium fs-14 fw-400 lh-17 wp-100"
              ></textarea>
              <div class="error-message" v-if="errors.suggestion">This is a required field.</div>
            </div>
            <div class="mt-8 fw-400 fs-16 lh-24 fs-16 lh-24 gray-700 mb-fs-14 mb-lh-20">
              <span>A few ideas to get you started: </span>
              <ul class="px-16 py-4">
                <li>Are there any special sites or activities you'd like to see or do?</li>
                <li>Is this trip for a specific occasion or event?</li>
                <li>Is there anything that made a previous trip extra special?</li>
                <li>Is there anything else you'd like to tell us about this trip?</li>
              </ul>
            </div>
            <div v-if="error" class='pb-30 red error-bottom'>{{ error }}</div>
          </form>
        </div>
      </div>
    </div>
    <FooterSteps
      :disableSubmitButton="disableSubmitButton"
      :labelClass="formData.labelClass"
      :stepActive="EnquiryStepEnum.YourStyle"
      :isUIPopup='formData.isUIPopup'
      :button-id="`enquiry-form-button-continue-step${indexCurrentStep}`"
      @nextStep="nextStep"
      @prevStep="$emit('prevStep')"
    />
  </div>
</template>
<script setup lang="ts">
import FooterSteps from '~/components/pages/Enquiry/MultiStepsForm/Steps/FooterSteps.vue'
import { DESKTOP } from '~/constant/common'
import { EnquiryStepEnum } from '~/enum/enquiry'
import { isDeepEqual, isEmpty } from '~/helper'
import { getEnquiryID } from '~/helper/enquiry'
import { debounceAmplitudeScriptTracking } from '~/mixins/amplitude'
import useFormValidation from '~/utils/validators'
import { ref, computed, watch, reactive, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useEnquiryCaptureStore } from '~/store/pinia/enquiryCapture'
import { useGeneralInfoStore } from '~/store/pinia/generalInfo'

defineProps({
  formData: { type: Object, default: () => {} },
})
const emits = defineEmits(['nextStep', 'prevStep'])

const route = useRoute()
const enquiryCaptureStore = useEnquiryCaptureStore()
const dataStep = reactive({
  suggestion: '',
})
const disableSubmitButton = ref(false)
const error = ref('')
const generalStore = useGeneralInfoStore()

const { validateNameField, errors } = useFormValidation()

const arrayStepEnquiry = computed(() => enquiryCaptureStore.arrayStep)
const dataStepYourTrip = computed(() => {
  return enquiryCaptureStore.enquiryDataByStep(route.path, EnquiryStepEnum.YourTrip)
})

const fromTripPlan = computed(() => route.name === 'enquiry-submit-trip' || route.name === 'trip-plan')

const labelFieldStyle = computed(() => {
  if (fromTripPlan.value) return "Is there anything you'd like to remove or add from the selected itinerary?"
  if (dataStepYourTrip.value && dataStepYourTrip.value.destination) {
    const destinationName = dataStepYourTrip.value.destination.map((v: any) => v.name).join(', ')
    return `Essential experiences to make the perfect trip while you're in ${destinationName}`
  }
  return ''
})
const windowWidth = computed(() => generalStore.generalData.windowWidth)
const isDesktop = computed(() => windowWidth.value > DESKTOP)
const stepOld = computed(() => enquiryCaptureStore.stepsData)
const stepBefore = computed(() => {
  if (stepOld.value && stepOld.value[route.path.toString()] && stepOld.value[route.path.toString()][EnquiryStepEnum.YourStyle])
    return stepOld.value[route.path.toString()][EnquiryStepEnum.YourStyle]
  return {}
})
const indexCurrentStep = computed(() => arrayStepEnquiry.value.indexOf(EnquiryStepEnum.YourStyle) + 1)

watch(dataStep, (val) => {
  if (!isEmpty(val.suggestion)) validateNameField('suggestion', val.suggestion)
})

const nextStep = async () => {
  validateNameField('suggestion', dataStep.suggestion)

  if(!errors.suggestion) {
    disableSubmitButton.value = true
    try {
      const payload = {
        path: route.path.toString(),
        step: EnquiryStepEnum.YourStyle,
        [EnquiryStepEnum.YourStyle]: {
          ...dataStep,
        },
      }
      
      const stepEdit = isDeepEqual(stepBefore.value, dataStep)
      
      if (!stepEdit) {
        const step = await enquiryCaptureStore.submitEnquiryCapture({ payload, dataStep })
        if (step?.status === 200) {
          debounceAmplitudeScriptTracking("View Enquiry (step 5)", {
            enquiry_id:  getEnquiryID(route.path)
          });
          error.value = ''
          emits('nextStep')
        } else {
          error.value = step?.message || ''
          throw new Error(`submit enquiry step ${EnquiryStepEnum.YourStyle} error (data):`, {
            cause: {
              data: step,
              payload: payload
            }
          })
        }
      } else if(!error.value) {
        emits('nextStep')
      }
    } catch (error) {
      console.log({ error })
      throw new Error(`submit enquiry step ${EnquiryStepEnum.YourStyle} error:`, { cause: error })
    } finally {
      disableSubmitButton.value = false
    }
  }
}

onMounted(() => {
  const data = stepBefore.value

  if (data) {
    if(data.suggestion) dataStep.suggestion = data.suggestion
  }
})
</script>
<style lang="scss" scoped>
// v2 (§Accessibility): restore the green keyboard focus ring on the bare textarea
textarea {
  background: $surface; // $white in light (was implicit UA default); #0D0D0D dark (was dark-scoped) — §9 input surface
  @include focus-ring;
  // W7 dark: form controls don't inherit body color — foreground text on the dark field. Border already flips via .bd-gray-medium.
  [data-theme='dark'] & {
    color: $foreground;
  }
}
ul {
  list-style-type: disc;

  li {
    margin-left: 8px;
    margin-bottom: 6px;
  }
}
</style>
